import { Button, Dialog, Field } from 'Components/Core';
import { IDialogProps } from 'Components/Core/Dialog/types';
import { SectionGroup } from 'Components/Layout';
import { LaboratoryDropdown, StandaloneEquipmentDropdownField } from 'Components/List';
import { WorksheetGridQueryInjectedPropsProvider } from 'Components/WorksheetGrids/Core';
import { WorksheetGridKeys } from 'Components/WorksheetGrids/WorksheetGridDefinitions';
import { actions } from 'Data/ReduxViewModels/WorksheetGrid';
import { hasValue } from 'Lib/Any';
import { useHasChanged } from 'Lib/Hooks';
import deepEquals from 'Lib/Object/deepEquals';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

/**
 * Dialog that lets the user configure the batch update page
 * by identifying the test method to update and selecting the fields to update
 */
const WorksheetBalanceConfigurationDialog: React.FC<IDialogProps> = ({ onClose, visible }) => {
  const laboratoryId = useSelector((state) => state.worksheetGrid.query.params.LaboratoryId);
  const homeLaboratoryId = useSelector((state) => state.user.data.HomeLaboratory.Id);
  const syncingBalance = useSelector((state) => state.worksheetGrid.status.syncingBalance);
  const currentPageType = useSelector((state) =>
    state.worksheetGrid.gridType === WorksheetGridKeys.WetMassEntry ? 'Wet' : 'Dry'
  );
  const defaultLaboratoryBalance = useSelector(
    (state) => state.worksheetGrid.additionalData['LaboratoryDefaultBalance'],
    deepEquals
  );
  const [balance, setBalance] = useState<any>(defaultLaboratoryBalance);
  const dispatch = useDispatch();

  const syncingBalanceHasChanged = useHasChanged(syncingBalance);
  useEffect(() => {
    if (!syncingBalance && syncingBalanceHasChanged) onClose?.();
  }, [syncingBalance, syncingBalanceHasChanged, onClose]);

  useEffect(() => {
    if (hasValue(defaultLaboratoryBalance)) setBalance(defaultLaboratoryBalance);
  }, [defaultLaboratoryBalance]);

  return (
    <Dialog
      title={'Configure ' + currentPageType + ' Mass Entry Page'}
      width={500}
      visible={visible}
      buttons={[
        <Button
          primary
          key="1"
          disabled={!balance}
          onClick={() => {
            dispatch(
              actions.setCommonDataValue({
                key: currentPageType === 'Wet' ? 'MoistureContentTest.Balance' : 'Measurement.Balance',
                value: balance,
              })
            );
            dispatch(
              actions.postDefaultBalance({
                balanceUUID: balance.Id,
                laboratoryId,
              })
            );
          }}
          loading={syncingBalance}
        >
          Done
        </Button>,
        <Button
          key="0"
          onClick={() => {
            onClose?.();
          }}
        >
          Cancel
        </Button>,
      ]}
    >
      <SectionGroup>
        <WorksheetGridQueryInjectedPropsProvider>
          <Field
            horizontal
            label="Laboratory"
            input={<LaboratoryDropdown />}
            name="LaboratoryId"
            simpleValue
            isClearable={false}
            autoSelectSingleOption
            searchImmediately
            autoSelectedValue={homeLaboratoryId}
          />
        </WorksheetGridQueryInjectedPropsProvider>
        <StandaloneEquipmentDropdownField
          horizontal
          label="Default Balance"
          equipmentType="balance"
          onChange={(e) => setBalance(e.data.value)}
          value={balance}
          laboratoryId={laboratoryId}
          disabled={!laboratoryId}
        />
      </SectionGroup>
    </Dialog>
  );
};

export { WorksheetBalanceConfigurationDialog };
export default WorksheetBalanceConfigurationDialog;
